import jwtDecode from 'jwt-decode'
const removeTrailingSlash = url => (url ? url.replace(/\/$/, '') : url)

class AxiosAPI {
  #APIHandler
  #baseURL
  #browserBaseURL

  constructor(APIHandler, baseURL, browserBaseURL) {
    this.#APIHandler = APIHandler
    this.#baseURL = removeTrailingSlash(baseURL)
    this.#browserBaseURL = removeTrailingSlash(browserBaseURL) || this.baseURL
  }
  request(config) {
    if (!navigator.onLine) throw new Error('No internet connection')
    const { baseURL } = this
    return this.#APIHandler.$request({ ...config, baseURL })
  }
  get(url, config) {
    return this.request({ ...config, url, method: 'get' })
  }
  delete(url, config) {
    return this.request({ ...config, url, method: 'delete' })
  }
  head(url, config) {
    return this.request({ ...config, url, method: 'head' })
  }
  options(url, config) {
    return this.request({ ...config, url, method: 'options' })
  }
  post(url, data, config) {
    return this.request({ ...config, url, method: 'post', data })
  }
  put(url, data, config) {
    return this.request({ ...config, url, method: 'put', data })
  }
  patch(url, data, config) {
    return this.request({ ...config, url, method: 'patch', data })
  }
  get baseURL() {
    return process.isBrowser ? this.#browserBaseURL : this.#baseURL
  }
}

class MultiAPI {
  #axios
  #dead
  #token
  #tokenMetadata
  #isRefreshing
  #refreshHandler
  #onFailRefresh
  #queue = []

  constructor(config) {
    const {
      axios,
      apis,
      refreshHandler,
      onFailRefresh,
      requestInterceptors = [],
      responseInterceptors = [],
    } = config
    Object.entries(apis).forEach(api => this.#injectApi(api))
    this.#isRefreshing = false
    this.#axios = axios
    this.#refreshHandler = refreshHandler
    this.#onFailRefresh = onFailRefresh
    requestInterceptors.forEach(interceptor =>
      axios.interceptors.request.use(interceptor.format, interceptor.error)
    )
    responseInterceptors.forEach(interceptor =>
      axios.interceptors.response.use(interceptor.format, interceptor.error)
    )
  }
  $kill() {
    this.#dead = true
  }
  #injectApi = function([name, config]) {
    const { baseURL, browserBaseURL } = config
    this[name] = new AxiosAPI(this, baseURL, browserBaseURL)
  }
  #request = function(config, customToken) {
    if (this.#dead) return new Promise(() => null)
    const token = customToken || this.#token
    if (token && !config.noAuth) {
      if (!config.headers) config.headers = {}
      config.headers['x-access-token'] = `${this.#token} `
    }
    return this.#axios.request(config)
  }
  #refreshToken = async function() {
    try {
      const newToken = await this.#refreshHandler()
      this.$setToken(newToken)
      this.#isRefreshing = false
      let cb
      while ((cb = this.#queue.shift())) {
        cb(newToken)
      }
      return newToken
    } catch (e) {
      let cb
      while ((cb = this.#queue.shift())) {
        cb(null, e)
      }
      this.#isRefreshing = false
      if (this.#onFailRefresh) this.#onFailRefresh(e)
      return null
    }
  }
  #tokenizedRequest = function(config) {
    if (!this.#isRefreshing) return this.#request(config)
    return new Promise((resolve, reject) =>
      this.#queue.push((token, err) => {
        if (err) return reject(err)
        this.#request(config, token)
          .then(resolve)
          .catch(reject)
      })
    )
  }
  get $tokenExpired() {
    const metadata = this.#tokenMetadata
    if (!metadata) return false
    const { exp } = metadata
    const expiration = exp * 1000
    const today = Date.now() + 30000
    return expiration < today
  }
  get $axios() {
    return this.#axios
  }
  set token(token) {
    this.$setToken(token)
  }
  get token() {
    return this.#token
  }
  $forceRefreshToken() {
    return this.#refreshToken()
  }
  $setToken(token) {
    this.#token = token
    this.#tokenMetadata = token ? jwtDecode(token) : null
  }
  $request(config) {
    const { $tokenExpired } = this
    const { ignoreExpiredToken, noAuth } = config
    if (ignoreExpiredToken || noAuth) return this.#request(config)

    if (this.#refreshHandler && !this.#isRefreshing && $tokenExpired) {
      this.#isRefreshing = true
      this.#refreshToken()
    }

    return this.#tokenizedRequest(config)
  }
}

export default ({ axiosConfig }) => new MultiAPI(axiosConfig)
